import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, TransitionGroup as _TransitionGroup, withCtx as _withCtx, createElementVNode as _createElementVNode, unref as _unref, mergeProps as _mergeProps, createBlock as _createBlock, withDirectives as _withDirectives, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "best-price-calendar" }
const _hoisted_2 = { class: "best-price-calendar__month-wrapper" }
const _hoisted_3 = { class: "best-price-calendar__month-left" }
const _hoisted_4 = { class: "best-price-calendar__month-right" }
const _hoisted_5 = { class: "best-price-calendar__wrap" }

import { ObserveVisibility as vObserveVisibility } from 'vue-observe-visibility';
import { now, formatDate, dateDiff } from '@/js/utils/dateTimeUtils';
import type { SelectionType } from '@/components/common/types';
import type { BestAccommodationOfferForCheckInDate } from '@/interfaces/api/v1-best-accommodation-offer-for-check-in-date';
import { useSwipe } from '@vueuse/core';
import type { SwipeDirection } from '@vueuse/core';
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from '@/components/common/store';
import type { MonthData } from '@helper/types/commonProps';
import Pager from '../Pager/Pager.vue';
import Month from '../Month/Month.vue';
import { storeToRefs } from 'pinia';
import { useBreakpointStore } from 'src/store/breakpointsStore';

interface Props {
	maxMonths?: number;
	offers: BestAccommodationOfferForCheckInDate[];
	startDate: Date;
	loading: boolean;
}

const PRICE_DISPLAY = 'Preisdarstellung: Gesamtpreis';
const CHEAPEST_OFFER = '= Günstigstes Angebot';


export default /*@__PURE__*/_defineComponent({
  __name: 'BestPriceCalendar',
  props: {
    maxMonths: { default: 24 },
    offers: {},
    startDate: {},
    loading: { type: Boolean }
  },
  emits: ['selectDay', 'changeMonth', 'input'],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const selection = ref<SelectionType>({
	from: 0,
	to: 0,
});

const store = useStore();

const visibleOffers = ref<BestAccommodationOfferForCheckInDate[]>([]);

const nowDate = ref<Date>(now() as Date);

const currentMonth = ref(nowDate.value.getMonth());

const currentYear = ref<number>(nowDate.value.getFullYear());

const monthsToShow = ref<MonthData[]>([]);

const visibleMonthIndex = ref(Math.max(0, dateDiff(nowDate.value, props.startDate, 'month')));

const calendarTransform = ref<Record<string, string>>({ transform: 'translateX(0%)' });

const { isMinTablet } = storeToRefs(useBreakpointStore());

const createRange = (start: number, length: number): number[] => Array.from({ length }, (_value, index) => index + start);

const getInitialMonthNumbers = (): number[] => {
	let untilMonth = visibleMonthIndex.value;
	if (isMinTablet.value) {
		untilMonth++;
	}

	return createRange(currentMonth.value, untilMonth + 1);
};

const updateCalendarScrollPosition = (): void => {
	const scrollHorizontal = visibleMonthIndex.value * -(isMinTablet.value ? 50 : 100);
	calendarTransform.value = {
		transform: `translateX(${scrollHorizontal}%)`,
	};
};
const emit = __emit;

const model = computed<SelectionType>({
	get() {
		const filter = store.getters.filter;
		return { from: +new Date(filter), to: +new Date(filter) };
	},
	set(newValue) {
		selection.value = newValue;
		emit('input', newValue);
	},
});

const onSelect = (day: Date) => {
	const date = day.getTime();
	model.value.from = date;
	model.value.to = date;
	emit('selectDay', day.toString());
};

const clearSelection = () => {
	model.value.from = 0;
	model.value.to = 0;
};

const emitChangeMonthEvent = (direction: number) => {
	let addToIndex;
	if (direction > 0) {
		addToIndex = isMinTablet.value ? 2 : 1;
	} else {
		addToIndex = -2;
	}
	const monthToShowEmitIndex = visibleMonthIndex.value + addToIndex;
	if (monthToShowEmitIndex >= 0) {
		emit('changeMonth', monthsToShow.value[monthToShowEmitIndex]);
	}
};

const filterVisibleOffers = (monthData: MonthData) => {
	visibleOffers.value.push(
		...props.offers.filter((offer) => {
			const offerDate = new Date(offer.StartDate);
			return offerDate.getMonth() === monthData.month && offerDate.getFullYear() === monthData.year;
		})
	);
};

const cheapestOffer = computed((): number | undefined => {
	if (visibleOffers.value.length) {
		const prices = visibleOffers.value.map((offer) => offer.Price);
		return Math.round(Math.min(...prices));
	}
	return undefined;
});

const setMonthActive = (index: number) => {
	const month = monthsToShow.value[index];
	if (month) {
		month.active = true;
		filterVisibleOffers(month);
	}
};

const updateVisibleMonths = () => {
	monthsToShow.value.forEach((month) => {
		month.active = false;
	});
	visibleOffers.value = [];
	setMonthActive(visibleMonthIndex.value);
	if (isMinTablet.value) {
		setMonthActive(visibleMonthIndex.value + 1);
	}
};

const onSlide = (direction: number) => {
	emitChangeMonthEvent(direction);
	visibleMonthIndex.value = Math.max(0, Math.min(visibleMonthIndex.value + direction, monthsToShow.value.length));
	updateCalendarScrollPosition();
	updateVisibleMonths();
};

const getMonth = (offset: number): MonthData => {
	const date = new Date(currentYear.value, offset);
	return {
		year: date.getFullYear(),
		month: date.getMonth(),
		offset,
		active: false,
	};
};

const createInitialMonthToShow = () => {
	const monthNumbers = getInitialMonthNumbers();
	monthsToShow.value = monthNumbers.map((number) => getMonth(number));
	monthsToShow.value.forEach((month) => {
		month.active = true;
		filterVisibleOffers(month);
	});
};

const getMonthString = (index: number): string => {
	const monthName = formatDate(new Date(2000, index, 1), { month: 'long' });
	return `${monthName}`;
};

const visibilityChanged = (month: MonthData, index: number) => {
	// lookahead 2 visible month
	const count = monthsToShow.value.length;
	if (index + 2 > count && count < props.maxMonths) {
		const next = getMonth(month.offset + 1);
		monthsToShow.value.push(next);
	}
};

const swiper = ref<HTMLElement | null>(null);

useSwipe(swiper, {
	onSwipeEnd(e: TouchEvent, direction: SwipeDirection) {
		if (direction === 'RIGHT') onSlide(-1);
		if (direction === 'LEFT') onSlide(1);
	},
});

watch(isMinTablet, () => {
	updateCalendarScrollPosition();
	updateVisibleMonths();
});

onMounted(() => {
	createInitialMonthToShow();
	updateCalendarScrollPosition();
	updateVisibleMonths();
});

__expose({
	clearSelection,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(Pager, {
          disabled: visibleMonthIndex.value === 0 || _ctx.loading,
          direction: "left",
          "aria-label": "Vorheriger Monat",
          class: "best-price-calendar__pager is-prev",
          onSlide: onSlide
        }, null, 8 /* PROPS */, ["disabled"]),
        _createVNode(_TransitionGroup, {
          tag: "span",
          name: "transition-month-change",
          class: "best-price-calendar__month-name"
        }, {
          default: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(monthsToShow.value, (month, index) => {
              return (_openBlock(), _createElementBlock(_Fragment, null, [
                (month.month + (month.year - currentYear.value) * 12 === visibleMonthIndex.value + currentMonth.value)
                  ? (_openBlock(), _createElementBlock("span", { key: index }, _toDisplayString(getMonthString(month.month)) + " " + _toDisplayString(month.year), 1 /* TEXT */))
                  : _createCommentVNode("v-if", true)
              ], 64 /* STABLE_FRAGMENT */))
            }), 256 /* UNKEYED_FRAGMENT */))
          ]),
          _: 1 /* STABLE */
        })
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createVNode(_TransitionGroup, {
          tag: "span",
          name: "transition-month-change",
          class: "best-price-calendar__month-name"
        }, {
          default: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(monthsToShow.value, (month, index) => {
              return (_openBlock(), _createElementBlock(_Fragment, null, [
                (month.month + (month.year - currentYear.value) * 12 === visibleMonthIndex.value + currentMonth.value + 1)
                  ? (_openBlock(), _createElementBlock("span", { key: index }, _toDisplayString(getMonthString(month.month)) + " " + _toDisplayString(month.year), 1 /* TEXT */))
                  : _createCommentVNode("v-if", true)
              ], 64 /* STABLE_FRAGMENT */))
            }), 256 /* UNKEYED_FRAGMENT */))
          ]),
          _: 1 /* STABLE */
        })
      ]),
      _createVNode(Pager, {
        disabled: visibleMonthIndex.value === monthsToShow.value.length - (_unref(isMinTablet) ? 2 : 1) || _ctx.loading,
        direction: "right",
        "aria-label": "Nächster Monat",
        class: "best-price-calendar__pager is-next",
        onSlide: onSlide
      }, null, 8 /* PROPS */, ["disabled"])
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", {
        ref_key: "swiper",
        ref: swiper,
        class: "best-price-calendar__inner",
        style: _normalizeStyle(calendarTransform.value)
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(monthsToShow.value, (monthData, index) => {
          return _withDirectives((_openBlock(), _createBlock(Month, _mergeProps({
            key: index,
            ref_for: true,
            ref: "monthComponent"
          }, monthData, {
            class: ["best-price-calendar__month", { 'active-calendar': monthData.active }],
            selection: model.value,
            horizontal: true,
            offers: visibleOffers.value,
            "best-price-calendar": true,
            "cheapest-offer": cheapestOffer.value,
            onSelect: onSelect
          }), null, 16 /* FULL_PROPS */, ["class", "selection", "offers", "cheapest-offer"])), [
            [_unref(vObserveVisibility), {
						callback: (isVisible) => {
							isVisible && visibilityChanged(monthData, index);
						},
						once: true,
					}]
          ])
        }), 128 /* KEYED_FRAGMENT */))
      ], 4 /* STYLE */),
      _createElementVNode("div", { class: "best-price-calendar__price-display-wrapper" }, [
        _createElementVNode("p", { class: "best-price-calendar__price-display" }, _toDisplayString(PRICE_DISPLAY)),
        _createElementVNode("p", { class: "best-price-calendar__cheapest-offer" }, _toDisplayString(CHEAPEST_OFFER))
      ])
    ])
  ]))
}
}

})